import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, Loader2, MessageSquareText, ShoppingBag, Tag } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports`, { withCredentials: true });
      setReports(res.data);
    } catch {
      toast.error("Não foi possível carregar os relatórios.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-olive" />
      </div>
    );
  }

  return (
    <section data-testid="admin-reports-panel">
      <p className="mb-6 text-sm text-moss">
        Resumo gerado automaticamente todo dia às 8h com mensagens e vendas do site.
      </p>
      {reports.length === 0 ? (
        <div
          data-testid="admin-reports-empty"
          className="flex flex-col items-center gap-4 rounded-[2rem] border border-dashed border-linen bg-white/60 py-20 text-center"
        >
          <CalendarDays className="h-10 w-10 text-moss/50" />
          <p className="font-display text-xl text-ink">Nenhum relatório ainda</p>
          <p className="max-w-sm text-sm text-moss">
            O primeiro resumo automático aparece aqui amanhã de manhã.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {reports.map((r) => (
            <div
              key={r.date}
              data-testid={`admin-report-${r.date}`}
              className="rounded-[1.5rem] border border-linen bg-white p-6 shadow-sm"
            >
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-clay">
                {new Date(`${r.date}T12:00:00`).toLocaleDateString("pt-BR", {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                })}
              </p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-parchment p-3 text-center">
                  <MessageSquareText className="mx-auto h-4 w-4 text-olive" />
                  <p className="mt-1 font-display text-2xl font-semibold text-ink">
                    {r.messages_total}
                  </p>
                  <p className="text-[11px] text-moss">mensagens</p>
                </div>
                <div className="rounded-xl bg-parchment p-3 text-center">
                  <ShoppingBag className="mx-auto h-4 w-4 text-olive" />
                  <p className="mt-1 font-display text-2xl font-semibold text-ink">
                    {r.sales_count}
                  </p>
                  <p className="text-[11px] text-moss">vendas</p>
                </div>
                <div className="rounded-xl bg-parchment p-3 text-center">
                  <p className="mt-1 font-display text-xl font-semibold text-olive">
                    {r.sales_total_brl.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                  </p>
                  <p className="text-[11px] text-moss">faturado</p>
                </div>
              </div>
              {r.top_interests?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {r.top_interests.map((i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 rounded-full bg-linen/60 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-olive"
                    >
                      <Tag className="h-3 w-3" />
                      {i}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
